import Breadcrumbs from './Breadcrumbs';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { usePathname, useRouter } from '@/lib/navigation';
import { Button } from './button';
import UserProfileDropdown from './UserProfileDropdown';
import { useLocale, useTranslations } from '@/i18n';
import { cn } from '@/lib/utils';

export default function TopPanel() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  const t = useTranslations('navigation');
  const { locale } = useLocale();
  const logoSrc = locale === 'en' ? '/assets/logo-en.svg' : '/assets/logo.svg';

  const navigationItems = [
    {
      label: t('myTrips'),
      href: '/',
      icon: '/assets/icons/briefcase.svg',
      isActive: pathname === '/',
    },
    {
      label: t('myApprovals'),
      href: '/approvals',
      icon: '/assets/icons/document-check.svg',
      isActive: pathname === '/approvals',
    },
    // TODO: Reports nav item hidden for now
    // {
    //   label: t('reports'),
    //   href: '/reports',
    //   icon: '/assets/icons/document.svg',
    //   isActive: pathname === '/reports',
    // },
  ];

  return (
    <>
      <div className="sticky top-0 z-50 border-b border-b-[#ebebeb] bg-white">
        <div className="flex h-[3.5rem] w-full shrink-0 items-center justify-between bg-white p-4 md:h-[5.875rem] md:p-[2.5rem]">
          {/* Left: Mobile Menu Button + Logo */}
          <div className="flex items-center gap-3">
            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-gray-100 md:hidden"
              aria-label="Toggle menu"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>

            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img
                src={logoSrc}
                alt="Cellfie Logo"
                className="h-[1.5rem] w-[4.5rem] shrink-0 cursor-pointer md:h-[2.20988rem] md:w-[7.125rem]"
              />
            </Link>
          </div>

          {/* Center: Breadcrumbs - aligned with main content */}
          <div className="ml-[4rem] hidden flex-1 items-center px-[1.96856rem] md:flex">
            <Breadcrumbs />
          </div>

          {/* Right: Profile Avatar */}
          <div className="flex items-center">
            <UserProfileDropdown />
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div
        className={cn(
          'fixed inset-0 z-50 flex bg-[rgba(0,0,0,0.0)] opacity-0 transition-opacity duration-300 ease-out md:hidden',
          isMobileMenuOpen
            ? 'pointer-events-auto bg-[rgba(0,0,0,0.6)] opacity-100'
            : 'pointer-events-none'
        )}
        onClick={() => setIsMobileMenuOpen(false)}
      >
        <aside
          className={cn(
            'relative h-full w-[15.5rem] translate-x-[-100%] bg-[#f9f9f9] p-4 shadow-[2px_0_16px_rgba(0,0,0,0.12)] transition-transform duration-300 ease-out',
            isMobileMenuOpen && 'translate-x-0'
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <div className="mb-4 flex justify-end">
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-gray-100"
              aria-label="Close menu"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Add Trip Button */}
          <div className="mb-6">
            <Button
              className="flex h-[2.75rem] w-full cursor-pointer items-center justify-center gap-1 rounded-4xl px-4 py-2.5 text-sm leading-5 font-medium"
              variant="brand"
              size="none"
              onClick={() => {
                setIsMobileMenuOpen(false);
                router.push('/trips/new');
              }}
            >
              <img
                src="/assets/icons/plus.svg"
                alt="Add"
                className="flex items-center justify-center gap-2"
              />
              <span className="whitespace-nowrap">{t('addTrip')}</span>
            </Button>
          </div>

          {/* Navigation Items */}
          <nav className="flex flex-col">
            {navigationItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  'flex items-center gap-3 rounded-2xl px-3 py-4 transition-all duration-200',
                  item.isActive
                    ? 'bg-[#f1f1f1] text-[#000000]'
                    : 'text-[#666666] hover:bg-[#f1f1f1] hover:text-[#000000]'
                )}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <div className="relative flex h-6 w-6 shrink-0 items-center justify-center">
                  <img
                    src={item.icon}
                    alt={item.label}
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
                <span className="text-sm leading-5 text-black">{item.label}</span>
              </Link>
            ))}
          </nav>
        </aside>
      </div>
    </>
  );
}
